import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { fmtDuration } from "@/components/SentenceClock";

const when = (iso) => new Date(iso).toLocaleString("en-CA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
const mins = (m) => fmtDuration(m * 60);

function Line({ label, value, testId, strong }) {
  return (
    <div className="flex justify-between gap-4 py-2 text-sm" style={{ borderBottom: "1px solid var(--border)" }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span style={{ color: strong ? "var(--accent)" : "var(--text-primary)" }} data-testid={testId}>{value}</span>
    </div>
  );
}

function ClockRecordModal({ onClose }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/sentence/record").then(({ data }) => setR(data)).catch(() => setR({ error: true })); }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in" style={{ background: "rgba(0,0,0,0.75)" }} onClick={onClose} data-testid="clock-record-overlay">
      <div className="w-full max-w-xl p-8 max-h-[88vh] overflow-y-auto" style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="clock-record-modal">
        <div className="flex justify-between items-start mb-6">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>clock</span>
          <button onClick={onClose} data-testid="clock-record-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>[ close ]</button>
        </div>
        {!r ? <p className="text-sm" style={{ color: "var(--text-muted)" }}>…</p> : !r.started_at ? (
          <p className="text-sm tracking-widest" style={{ color: "var(--text-secondary)" }} data-testid="clock-record-not-started">the clock has not started yet</p>
        ) : (
          <>
            <Line label="started" value={when(r.started_at)} testId="clock-record-started" />
            <Line label="sentence set" value={mins(r.start_minutes)} testId="clock-record-set" />
            <Line label="time elapsed" value={`− ${fmtDuration(r.elapsed_seconds)}`} testId="clock-record-elapsed" />
            <Line label="time reduced" value={`− ${mins(r.reduced_minutes)}`} testId="clock-record-reduced" />
            <Line label="time extended" value={`+ ${mins(r.extended_minutes)}`} testId="clock-record-extended" />
            <Line label={r.paused ? "remaining · paused" : "remaining"} value={fmtDuration(r.remaining_seconds)} testId="clock-record-remaining" strong />
            <h3 className="text-xs tracking-widest mt-10 mb-2" style={{ color: "var(--text-muted)" }}>every change · newest first</h3>
            {!r.entries.length ? <p className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="clock-record-empty">no one has changed the clock yet</p> : (
              <ul data-testid="clock-record-list">
                {r.entries.map((e) => (
                  <li key={e.id} className="grid grid-cols-[1fr_auto] gap-x-4 py-3 text-sm" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`clock-record-entry-${e.id}`}>
                    <span style={{ color: "var(--text-primary)" }}>{e.user} <span style={{ color: "var(--text-muted)" }}>{e.direction === "reduce" ? "reduced" : "extended"}</span></span>
                    <span style={{ color: "var(--accent)" }}>{e.direction === "reduce" ? "−" : "+"} {mins(e.minutes)}</span>
                    <span className="text-xs col-span-2 mt-1" style={{ color: "var(--text-muted)" }}>{when(e.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function ClockRecordTitle() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="clock-record-btn" className="mt-10 font-head text-2xl tracking-[0.4em] underline underline-offset-8 decoration-1 transition-opacity hover:opacity-70" style={{ color: "var(--text-primary)" }}>
        clock
      </button>
      {open && createPortal(<ClockRecordModal onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}
